/**
 * useCapabilities Hook
 * 
 * Single source of truth for feature gating on the frontend
 * Replaces useFeatureAccess with server-driven capabilities
 */

import { useQuery } from '@tanstack/react-query';
import type { Capabilities, CountryCode } from '@shared/capabilities';
import { remainingFromUsageStatus, type UsageStatusResponse } from '@/lib/usageRemaining';

/**
 * Fetch capabilities from the API
 */
export function useCapabilities() {
  const query = useQuery<Capabilities>({
    queryKey: ['/api/capabilities'],
    staleTime: 5 * 60 * 1000, // 5 minutes - capabilities don't change often
    retry: 2,
  });

  const capabilities = query.data;

  // What is actually LEFT this month (the capabilities payload only carries caps).
  const usageQuery = useQuery<UsageStatusResponse>({
    queryKey: ['/api/usage/status'],
    staleTime: 60 * 1000,
    retry: 1,
  });
  const remaining = remainingFromUsageStatus(usageQuery.data);

  // Helper functions for common checks
  const helpers = {
    // Voice capabilities
    canMakeCall: capabilities?.voice.enabled && capabilities?.voice.outbound,
    canReceiveCall: capabilities?.voice.enabled && capabilities?.voice.inbound,
    canCallCountry: (country: CountryCode) => {
      if (!capabilities?.voice.enabled || !capabilities?.voice.outbound) {
        return false;
      }
      // Type-safe check: CountryCode is guaranteed to be valid
      return capabilities.voice.allowedCountries.includes(country);
    },
    canRecordCalls: capabilities?.voice.recording,
    canTransferCalls: capabilities?.voice.transfer,
    canConference: capabilities?.voice.conferencing,
    
    // SMS capabilities
    canSendSMS: capabilities?.sms.enabled && capabilities?.sms.outbound,
    canReceiveSMS: capabilities?.sms.enabled && capabilities?.sms.inbound,
    canSmsCountry: (country: CountryCode) => {
      if (!capabilities?.sms.enabled || !capabilities?.sms.outbound) {
        return false;
      }
      // Type-safe check: CountryCode is guaranteed to be valid
      return capabilities.sms.allowedCountries.includes(country);
    },
    canSendMMS: capabilities?.sms.mms,
    canRunCampaigns: capabilities?.sms.campaigns,
    
    // Phone numbers
    canPurchaseNumbers: capabilities?.phoneNumbers.canPurchase,
    canPortNumbers: capabilities?.phoneNumbers.canPort,
    hasNumbersAvailable: capabilities && 
      (capabilities.phoneNumbers.maxNumbers > 0),
    
    // Team features
    canManageTeam: capabilities?.team.enabled,
    canAddMembers: capabilities && 
      capabilities.team.enabled && 
      capabilities.team.maxMembers > 1,
    
    // AI features
    canUseAIVoice: capabilities?.ai.voiceAgent,
    canUseAIComposer: capabilities?.ai.messageComposer,
    canGetCallSummaries: capabilities?.ai.callSummaries,
    canTranscribe: capabilities?.ai.transcription,
    
    // Integrations
    canUseAPI: capabilities?.integrations.api,
    canUseWebhooks: capabilities?.integrations.webhooks,
    canUseIntegrations: capabilities?.integrations.enabled,
    
    // Analytics
    canAccessAnalytics: capabilities?.analytics,
    
    // Access level checks
    isLocked: capabilities?.accessPosture === 'locked' || 
              capabilities?.accessPosture === 'suspended',
    isRestricted: capabilities?.accessPosture === 'restricted',
    isTrialing: capabilities?.lifecycle === 'trialing',
    isActive: capabilities?.lifecycle === 'active',
    
    // Limits. *Remaining = left this month (null = no hard stop / unknown);
    // *Monthly = the plan cap.
    voiceMinutesRemaining: remaining.voiceMinutesRemaining,
    smsRemaining: remaining.smsRemaining,
    voiceMinutesMonthly: capabilities?.limits.voiceMinutesMonthly,
    smsMonthly: capabilities?.limits.smsMonthly,
    phoneNumbersRemaining: capabilities?.limits.phoneNumbers,
    teamMembersRemaining: capabilities?.limits.teamMembers,
    
    // Usage tracking (critical fields)
    numbersIncluded: capabilities?.usage.numbersIncluded || 0,
    numbersInUse: capabilities?.usage.numbersInUse || 0,
    numbersAvailable: capabilities ? 
      Math.max(0, capabilities.usage.numbersIncluded - capabilities.usage.numbersInUse) : 0,
    teamMembersIncluded: capabilities?.usage.teamMembersIncluded || 0,
    teamMembersInUse: capabilities?.usage.teamMembersInUse || 0,
    teamMembersAvailable: capabilities ? 
      Math.max(0, capabilities.usage.teamMembersIncluded - capabilities.usage.teamMembersInUse) : 0,
    
    // Account status (critical fields)
    balanceOK: capabilities?.balanceOK ?? false,
    hasMultipleUsers: capabilities?.hasMultipleUsers ?? false,
  };

  return {
    capabilities,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    ...helpers,
  };
}

/**
 * Helper hook to check if a specific feature is available
 * Useful for conditional rendering
 */
export function useFeature(feature: keyof Capabilities): boolean {
  const { capabilities, isLoading } = useCapabilities();
  
  if (isLoading || !capabilities) {
    return false;
  }
  
  return !!capabilities[feature];
}

/**
 * Helper hook to check if organization can contact a specific country
 */
export function useCountryAccess(country: CountryCode) {
  const { canCallCountry, canSmsCountry, isLoading } = useCapabilities();
  
  return {
    canCall: canCallCountry(country),
    canSms: canSmsCountry(country),
    isLoading,
  };
}
